/**
 * TexturePackLibrary.js
 * Bibliothek und Verwaltung fuer Texture Packs.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getAuthToken } from '../services/api';
import { useToastContext } from '../App';
import { useAuth } from '../contexts/AuthContext';

function TexturePackCard({ pack, isOwn, onDelete, onShare, onClone, onDownload, onApply, servers, isAdmin }) {
  const navigate = useNavigate();
  const [showApply, setShowApply] = useState(false);
  const [selectedServer, setSelectedServer] = useState('');
  const [requirePack, setRequirePack] = useState(false);

  return (
    <div className="texture-pack-card card">
      <div className="texture-pack-card-header">
        <div className="texture-pack-icon">
          {/* Fallback-Icon */}
          <div style={{
            width: 48, height: 48, background: '#2a2a3a', borderRadius: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24
          }}>
            🎨
          </div>
        </div>
        <div className="texture-pack-card-title">
          <h3>{pack.name}</h3>
          <div className="flex gap-2" style={{ flexWrap: 'wrap', marginTop: 4 }}>
            <span className="tag">MC {pack.mcVersion}</span>
            <span className="tag">pack_format {pack.packFormat}</span>
            <span className="tag">{pack.textureCount} Texturen</span>
            {pack.isShared && <span className="tag tag-shared">Geteilt</span>}
          </div>
        </div>
      </div>

      {pack.description && (
        <p className="text-muted" style={{ fontSize: 14, marginTop: 8 }}>{pack.description}</p>
      )}

      <div className="texture-pack-card-footer">
        <div className="text-sm text-muted">
          Aktualisiert: {new Date(pack.updatedAt).toLocaleDateString('de-DE')}
        </div>
        <div className="flex gap-2" style={{ flexWrap: 'wrap', marginTop: 8 }}>
          {isOwn && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate(`/texture-packs/${pack.id}`)}
            >
              Bearbeiten
            </button>
          )}
          {!isOwn && (
            <button className="btn btn-secondary btn-sm" onClick={() => onClone(pack)}>
              Kopieren
            </button>
          )}
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => onDownload(pack)}
          >
            ⬇ ZIP
          </button>
          {isOwn && (
            <button
              className={`btn btn-sm ${pack.isShared ? 'btn-warning' : 'btn-secondary'}`}
              onClick={() => onShare(pack)}
            >
              {pack.isShared ? '🔒 Privat' : '🔗 Teilen'}
            </button>
          )}
          {isOwn && isAdmin && servers && servers.length > 0 && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowApply(v => !v)}
              title="Als Server-Ressource-Pack setzen"
            >
              ⚙ Server
            </button>
          )}
          {isOwn && (
            <button className="btn btn-danger btn-sm" onClick={() => onDelete(pack)}>
              🗑
            </button>
          )}
        </div>

        {/* Server-Apply Panel */}
        {showApply && isAdmin && (
          <div className="server-apply-panel" style={{ marginTop: 12, padding: 12, background: '#1a1a2e', borderRadius: 8 }}>
            <p className="text-sm" style={{ marginBottom: 8 }}>
              <strong>Als Server Resource Pack setzen</strong>
            </p>
            <select
              className="mc-select"
              value={selectedServer}
              onChange={e => setSelectedServer(e.target.value)}
              style={{ width: '100%', marginBottom: 8 }}
            >
              <option value="">Server waehlen...</option>
              {servers.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <label className="flex items-center gap-2" style={{ marginBottom: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={requirePack}
                onChange={e => setRequirePack(e.target.checked)}
              />
              Resource Pack erzwingen (require-resource-pack=true)
            </label>
            <button
              className="btn btn-primary btn-sm"
              disabled={!selectedServer}
              onClick={() => {
                onApply(pack, selectedServer, requirePack);
                setShowApply(false);
              }}
            >
              Anwenden
            </button>
            <p className="text-sm text-muted" style={{ marginTop: 6 }}>
              Der Pack muss "Geteilt" sein damit der MC-Server ihn herunterladen kann.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function TexturePackLibrary() {
  const navigate = useNavigate();
  const addToast = useToastContext();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [ownPacks, setOwnPacks] = useState([]);
  const [sharedPacks, setSharedPacks] = useState([]);
  const [servers, setServers] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('own');
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPack, setNewPack] = useState({ name: '', description: '', mcVersion: '1.21.1' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [packData, versionData] = await Promise.all([
        api.getTexturePacks(),
        api.getTexturePackVersions(),
      ]);
      setOwnPacks(packData.own || []);
      setSharedPacks(packData.shared || []);
      setVersions(versionData || []);

      if (isAdmin) {
        const srvData = await api.getServers();
        setServers(srvData || []);
      }
    } catch (err) {
      addToast('Fehler beim Laden: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!newPack.name.trim()) {
      addToast('Name ist erforderlich', 'error');
      return;
    }
    setCreating(true);
    try {
      const created = await api.createTexturePack(newPack);
      setOwnPacks(prev => [created, ...prev]);
      setShowCreateModal(false);
      setNewPack({ name: '', description: '', mcVersion: '1.21.1' });
      addToast('Texture Pack erstellt!', 'success');
      navigate(`/texture-packs/${created.id}`);
    } catch (err) {
      addToast('Fehler: ' + err.message, 'error');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(pack) {
    if (deleteConfirm?.id === pack.id) {
      try {
        await api.deleteTexturePack(pack.id);
        setOwnPacks(prev => prev.filter(p => p.id !== pack.id));
        addToast('Pack gelöscht', 'success');
        setDeleteConfirm(null);
      } catch (err) {
        addToast('Fehler: ' + err.message, 'error');
      }
    } else {
      setDeleteConfirm(pack);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  }

  async function handleShare(pack) {
    try {
      await api.setTexturePackShared(pack.id, !pack.isShared);
      setOwnPacks(prev => prev.map(p => p.id === pack.id ? { ...p, isShared: !p.isShared } : p));
      addToast(pack.isShared ? 'Pack ist jetzt privat' : 'Pack wird geteilt', 'success');
    } catch (err) {
      addToast('Fehler: ' + err.message, 'error');
    }
  }

  async function handleClone(pack) {
    try {
      const cloned = await api.cloneTexturePack(pack.id);
      setOwnPacks(prev => [cloned, ...prev]);
      setActiveTab('own');
      addToast('Pack in deine Bibliothek kopiert!', 'success');
    } catch (err) {
      addToast('Fehler: ' + err.message, 'error');
    }
  }

  async function handleDownload(pack) {
    try {
      const url = api.downloadTexturePackUrl(pack.id);
      const token = getAuthToken();
      const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const safeName = (pack.name || 'texture_pack').replace(/[^a-zA-Z0-9_\-. ]/g, '_');
      a.download = `${safeName}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      addToast('Download-Fehler: ' + err.message, 'error');
    }
  }

  async function handleApply(pack, serverId, requireResourcePack) {
    if (!pack.isShared) {
      addToast('Der Pack muss zuerst geteilt werden (🔗 Teilen), damit der MC-Server ihn herunterladen kann.', 'warning');
      return;
    }
    try {
      const result = await api.applyTexturePackToServer(pack.id, serverId, {
        requireResourcePack,
        host: window.location.host,
      });
      addToast(result.message || 'Resource Pack gesetzt!', 'success');
    } catch (err) {
      addToast('Fehler: ' + err.message, 'error');
    }
  }

  const filteredOwn = ownPacks.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.description?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredShared = sharedPacks.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.description?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="page-content"><div className="empty-state"><div className="spinner" /></div></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2>Texture Pack Bibliothek</h2>
        <div className="flex items-center gap-2">
          <input
            type="text"
            className="mc-input"
            placeholder="Packs durchsuchen..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 220 }}
          />
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + Neuer Pack
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        <button className={`tab-btn ${activeTab === 'own' ? 'active' : ''}`} onClick={() => setActiveTab('own')}>
          Meine Packs ({ownPacks.length})
        </button>
        <button className={`tab-btn ${activeTab === 'shared' ? 'active' : ''}`} onClick={() => setActiveTab('shared')}>
          Geteilte Packs ({sharedPacks.length})
        </button>
      </div>

      {/* Eigene Packs */}
      {activeTab === 'own' && (
        <div>
          {filteredOwn.length === 0 ? (
            <div className="empty-state">
              <div className="icon">🎨</div>
              {search ? (
                <p>Keine Packs gefunden fuer "{search}"</p>
              ) : (
                <>
                  <p>Noch keine Texture Packs erstellt</p>
                  <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                    Ersten Pack erstellen
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="texture-pack-grid">
              {filteredOwn.map(pack => (
                <TexturePackCard
                  key={pack.id}
                  pack={pack}
                  isOwn={true}
                  onDelete={handleDelete}
                  onShare={handleShare}
                  onClone={handleClone}
                  onDownload={handleDownload}
                  onApply={handleApply}
                  servers={servers}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Geteilte Packs */}
      {activeTab === 'shared' && (
        <div>
          {filteredShared.length === 0 ? (
            <div className="empty-state">
              <div className="icon">🔗</div>
              {search ? (
                <p>Keine geteilten Packs gefunden</p>
              ) : (
                <p>Noch keine Packs von anderen Nutzern geteilt</p>
              )}
            </div>
          ) : (
            <div className="texture-pack-grid">
              {filteredShared.map(pack => (
                <TexturePackCard
                  key={pack.id}
                  pack={pack}
                  isOwn={false}
                  onDelete={handleDelete}
                  onShare={handleShare}
                  onClone={handleClone}
                  onDownload={handleDownload}
                  onApply={handleApply}
                  servers={servers}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Erstellen-Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Neuen Texture Pack erstellen</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  className="mc-input"
                  value={newPack.name}
                  onChange={e => setNewPack(p => ({ ...p, name: e.target.value }))}
                  placeholder="z.B. Mein Custom Pack"
                  maxLength={64}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Beschreibung</label>
                <input
                  className="mc-input"
                  value={newPack.description}
                  onChange={e => setNewPack(p => ({ ...p, description: e.target.value }))}
                  placeholder="Optional..."
                  maxLength={256}
                />
              </div>
              <div className="form-group">
                <label>Ziel-Minecraft-Version</label>
                <select
                  className="mc-select"
                  value={newPack.mcVersion}
                  onChange={e => setNewPack(p => ({ ...p, mcVersion: e.target.value }))}
                >
                  {versions.length > 0 ? versions.map(v => (
                    <option key={v.version} value={v.version}>
                      {v.version} (pack_format {v.packFormat})
                      {v.cached ? ' ✓ Vanilla-Texturen verfuegbar' : ''}
                    </option>
                  )) : (
                    <>
                      <option value="1.21.4">1.21.4 (pack_format 46)</option>
                      <option value="1.21.1">1.21.1 (pack_format 34)</option>
                    </>
                  )}
                </select>
                <p className="text-sm text-muted" style={{ marginTop: 4 }}>
                  ✓ = Vanilla-Texturen als Vorlage verfuegbar (Admin muss Texturen zuerst herunterladen)
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Abbrechen</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
                {creating ? 'Erstellt...' : 'Pack erstellen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loeschen-Bestaetigung */}
      {deleteConfirm && (
        <div className="toast-container" style={{ bottom: 80 }}>
          <div className="toast warning">
            Nochmal klicken um "{deleteConfirm.name}" zu loeschen
          </div>
        </div>
      )}
    </div>
  );
}

export default TexturePackLibrary;
